/**
 * Game-start screen placement — the start-screen sibling of result-assembly-layout.js's
 * `placeResultAssembly` (docs/layout/ENGINE § Ray-based component ordering: a
 * placement pass downstream of the solver, not part of it), which since 2026-08-27 is a
 * module of the same shape rather than a pass buried in the result orchestration. The
 * start screen has
 * no game state and no oval, so it does NOT call `computeLayout` — it reuses only
 * the measure → fit → place pattern.
 *
 * One self-contained pass, `placeStartAssembly`:
 *   1. Decides the ARRANGEMENT from the viewport (engine owns the decision, not a CSS
 *      media query) and writes it as `data-arrangement`.
 *   2. Fits the composition toward a width target — a comfortable column (COMFY_MAX_PX
 *      on wide screens, SIDE_FILL of the viewport on narrow ones) — pressure-
 *      independent since the width decouple (start-screen-layout-improvements A1):
 *      `--gs-menu-width` is a pure function of `vw`, published once at the top of the
 *      pass and never revisited by the height solve below.
 *   3. Raises the height PRESSURE until the MENU fits the height cap (Play stays
 *      visible) through a small decision pipeline, each decision a PURE function of a
 *      canonicalized provisional solve, never re-evaluated against its own effect (no
 *      fixed-point iteration, so no oscillation can exist): canonicalize
 *      (`canonicalizeDecisionState`) → a bare provisional solve → the compact decision
 *      (`applyCompactDecision`, A1's demoted-to-styling residue: `data-compact` flips
 *      only the mode cards' axis and the control bank's alignment, never a width or an
 *      offset) → the strip decision (`decideStripWrap`, what CSS applies as
 *      `.gs-strip--wrap`) against the COMMITTED (post-compact) ux.
 *   4. Sizes the composition ELASTICALLY at that fit (slice 2): no transform — the
 *      sizes are pushed as real-px/unitless custom properties, so text renders crisp
 *      instead of softening under a `>1` transform. At rest (no height pressure) every
 *      part scales UNIFORMLY to the width fit — the design proportions hold. Under
 *      height pressure the deficit is distributed by the solver's group weights
 *      (docs/layout/ENGINE § weighted sizing groups): gaps/air (FILLER) yield 4× faster
 *      than tappable content (UX), the wordmark (VISUAL) 2× — the elastic-table move,
 *      "tuck the cards closer before you shrink them", simultaneous and continuous.
 *   5. Top-anchors the composition via the engine's own `--gs-top-inset` (CSS:
 *      `top: var(--gs-top-inset, 4vh)`) — a continuous formula treating the frame
 *      padding above the content as yieldable air, computed once the pipeline settles
 *      (`computeTopInset`); the leaderboard flows below at its own self-responsive
 *      size (it fills the stretch flex column with no width rule of its own now that
 *      the column is never crushed — the leaderboard's former width escape
 *      merged away), reachable by PAGE SCROLL — the stage's in-flow height is
 *      set to `topInset + content.offsetHeight` so the document scrolls.
 *
 * RE-FITS are cheap or deferred (start-screen-entrance-stutter, 2026-08-24). The pass
 * above is expensive by construction — `solveHeightPressure` writes four `--gs-*`
 * properties and reads back a measure per bisection step, ~16 forced recalc+layout
 * passes, doubled where the strip decision flips — and the content observer below
 * re-runs it on every `.game-start-content` size change, which on a data-rich session
 * means ~6 times DURING the entrance choreography (leaderboard section appearing, then
 * filling with rows; the multiplayer panel; turn banners). Measured on a Pixel 5 against
 * prod: ~5 main-thread freezes of 111–356ms inside the entrance's first ~2s. Two
 * mechanisms cut that, both keyed off `START_REFIT_DEFAULTS`:
 *   (a) `reservedWithoutResolving` — a re-fit whose inputs are provably unchanged does
 *       the cheap tail only (re-reserve the scroll height from one batched read),
 *       skipping the whole pipeline. Safe by CONSTRUCTION, not by enumerating what
 *       changed: the memo keys on the exact quantity the solve converges on — plus one
 *       cheap extra check, since the strip's labeled fit carries an input (label glyph
 *       widths) that quantity provably does NOT cover.
 *   (b) `refitAtEntranceSettle` — a re-fit that IS due while the entrance is in flight
 *       waits for the furniture-settled point and coalesces, so N mid-entrance triggers
 *       cost one re-fit, landing after the choreography rather than inside it
 *       (docs/PERFORMANCE.md § Choreography frames stay clean).
 */
import { SIZING_GROUPS } from "../constants.js";
import { applyMenuAffordanceVars } from "../layout/menu-affordance.js";
import { refreshGlareAngles } from "../animation/glare.js";
import { measureText } from "../prompt-ui.text-metrics.js";
import {
  onVisualViewportSettled,
  smallViewportHeight,
} from "../viewport-settle.js";
import { MULTIPLAYER_PANEL_SELECTOR } from "./game-start-multiplayer-section.js";

// The base (design, multiplier-1) width of the composition per arrangement — the SINGLE
// SOURCE for the rendered column width (pushed UX-scaled to CSS as `--gs-menu-width`) and
// the reflow floor below. Wide = the two mode cards side-by-side; stacked = the narrow
// one-column form.
const BASE_WIDTH = { wide: 560, stacked: 320 };

// The two re-fit economies (see the file header), as an overridable defaults object —
// the `ANIMATING_MIRROR_DEFAULTS` / `GLARE_DEFAULTS` shape. Both ship on; turning them
// off restores the pre-fix behaviour exactly, which is what the device lab's
// `norefitskip` inverse arm (scripts/dev/android/device-ab.js) and the skip-path unit
// tests both need — a tripwire that can force the expensive path back on proves the
// cheap one still bites, instead of reading a green null result.
export const START_REFIT_DEFAULTS = {
  skipUnchangedSolves: true,
  deferToEntranceSettle: true,
};

// play-test: the side breathing the two mode cards need before they must stack. The reflow
// floor is DERIVED (`BASE_WIDTH.wide` + this), not a standalone magic — change the base and
// the breakpoint follows.
const REFLOW_MARGIN_PX = 140;

// play-test: the widest the menu column ever RENDERS (the max-width cap slice 1 removed,
// reborn). Tuned so a desktop-ref viewport lands the menu near 1/φ² of its width; below
// this the menu fills the viewport width instead (SIDE_FILL).
const COMFY_MAX_PX = 980;

// play-test: on viewports narrower than the comfy column, fill this much of the width —
// bare side padding, so a phone-portrait menu isn't a lost stamp.
const SIDE_FILL = 0.92;

// play-test: the MENU (not the leaderboard) may occupy this much of the viewport height —
// the cap that keeps Play visible on a short viewport (phone-landscape); the leaderboard
// below overflows into page scroll.
const HEIGHT_FILL = 0.9;

// play-test: the menu's top anchor as a fraction of viewport height at rest — mirrors
// the old static CSS `top: 4vh`; `computeTopInset` reproduces it exactly at rest
// (pressureYield = 1) and departs from it under pressure.
const TOP_GAP_FRAC = 0.04;

// why: mirrors #skat-app's own `--space-xl` top padding (skat.css § Layout) — a
// mirrored constant with a sync comment, the same class as TOP_GAP_FRAC mirroring the
// old CSS `top: 4vh`. `computeTopInset` treats this padding as yieldable air.
const APP_FRAME_TOP_PX = 24;

// play-test: the least top gap `.game-start-content` is ever pushed to under deep
// height pressure — `computeTopInset`'s floor.
const TOP_FLOOR_PX = 8;

// play-test: the canonical (pre-compact) provisional ux below which a wide-arrangement
// viewport engages compact card styling (A1 pipeline step 4) — canonical ux at the
// motivating 740×360 viewport measures 0.541 (§ Verification, the amended spec), so
// this starting value engages with wide margin.
const COMPACT_UX_THRESHOLD = 0.95;

// The reference strip's own geometry, mirrored from its CSS (skat.css § Start-Screen Reference
// Strip / § Multiplayer Page's Start-screen section — `.gs-strip-tile > svg`, `.gs-strip-tile-
// label`, `.gs-strip-tile`'s own gap/padding) — this file and the stylesheet must agree on these
// numbers or fitsLabeled's answer stops matching what the browser actually renders.
//
// Post-review amendment (Andre-approved, live in-browser A/B, 2026-08-11): the pure
// base-px-times-multiplier contract this file originally shipped with COULD NOT render the
// approved mock composition — the mocks used near-constant absolute sizes across viewports,
// while `--gs-ux` spans ~1.12–1.75, so pure multiplication either overshoots at every wide
// viewport (the original 44/14 finding: `.gs-strip--wrap` was the observed state everywhere,
// never the labeled single row) or undershoots at narrow ones. Each size below is now a
// `min(plateau, base * ux)` shape — `plateauedSize()` — the § Leaderboard-internals precedent
// (engine-set width, self-responsive internals) applied to the strip: the multiply still runs
// here, in the engine, but clamps at a near-constant plateau once ux crosses the
// plateau/base ratio, so desktop widths render close to the approved absolute anchors while
// narrow ones still ride the multiplier's own natural shrink — and pressure's continued
// unconditional collapse toward zero (PRESSURE_FLOORS.UX = 0) still holds, since `base * ux`
// keeps shrinking under the plateau exactly as before once ux drops low enough.
const STRIP_ICON_PLATEAU_PX = 28; // play-test: desktop-anchor icon size
const STRIP_ICON_BASE_PX = 22; // play-test: portrait effective, tuned tighter than the 24
// anchor's own naive base/portraitUx division — measured live: at 352×638 the WHOLE
// composition (not just the strip) still needs real height pressure even with the strip's
// own smaller footprint, so --gs-menu-width renders materially narrower than the REST target
// fitsLabeled's wrap decision uses. Requirement (a) (no ellipsis) outranks (c) (land near the
// anchor). Review finding (this task, second round): the OTHER four constants below were
// briefly tightened too (padding/row-gap) to close a ~1-2px margin gap in the wrapped 3×2
// grid at low ux — but that pushed their own crossover ux BELOW the 1.75 desktop ceiling,
// silently dropping desktop padding/gap off their exact anchors (a real (c) regression the
// review caught). Reverted: padding/row-gap stay at their original bases (still exactly
// clamped at every desktop-class ux), and the icon absorbs the extra margin instead — it has
// by far the most headroom before its own crossover (28/24 ≈ 1.17) reaches the 1.75 ceiling,
// so tightening it further costs nothing at desktop and still buys real room at low ux.
const STRIP_LABEL_PLATEAU_PX = 12.5; // play-test: desktop-anchor label size
const STRIP_LABEL_BASE_PX = 9.5; // play-test: portrait effective — the binding constraint
// (longest real label, both locales, at the tightest measured column) sets this, not the
// anchor's own ≈11
const STRIP_TILE_GAP_PLATEAU_PX = 8; // play-test: desktop-anchor icon↔label gap
const STRIP_TILE_GAP_PX = 3.5; // play-test: portrait effective, tightened for (a)
const STRIP_TILE_PADDING_PLATEAU_PX = 8; // play-test: desktop-anchor tile padding (one side)
const STRIP_TILE_PADDING_PX = 4.6; // play-test: portrait effective, tightened for (a) — no
// separate portrait anchor was given; tunable independent of tile-gap if a future pass wants
// them to diverge. why: 4.6 is the largest reduction from --space-sm (8px) that still clamps
// to the desktop plateau at the 1.75 ceiling (4.6 * 1.75 = 8.05 → 8) — going lower drops
// desktop off its exact anchor (review finding, this task's second round; see the icon
// constant's comment for the fuller account)
const STRIP_ROW_GAP_PLATEAU_PX = 10; // play-test: desktop-anchor inter-tile grid gap
const STRIP_ROW_GAP_PX = 6; // play-test: portrait effective, tightened for (a). why: 6 is the
// largest reduction that still clamps to the desktop plateau at the 1.75 ceiling
// (6 * 1.75 = 10.5 → 10) — same constraint as the tile-padding constant above
// The tile's inherited `.icon-tile` border (skat.css: `border: 1px solid`) — NOT `* ux`, unlike
// every other geometry term above: box-sizing: border-box eats it out of the tile's own content
// width regardless of the elastic multiplier, since the border-width itself never scales with
// --gs-ux. Fixed at both sides' worth (review finding: omitting this made fitsLabeled ~12px
// (6 tiles × 2px) more optimistic than the real six-tile row actually renders).
const STRIP_TILE_BORDER_PX = 2;

/** `min(plateau, base * ux)` — the strip's own sizing shape (see the constants above): plateaus
 *  at `plateauPx` once `ux` crosses `plateauPx / basePx`, otherwise rides the multiplier like
 *  every other elastic consumer on this screen. Mirrors the CSS `min()` expressions exactly —
 *  keep both sides of every pair in sync, or fitsLabeled's answer stops matching what renders. */
export function plateauedSize(basePx, plateauPx, ux) {
  return Math.min(plateauPx, basePx * ux);
}

/** Decide the arrangement from the viewport width — stacked when the wide base plus its
 *  side breathing can't fit. Engine owns the decision; CSS applies it. */
function arrangementFor(vw) {
  return vw < BASE_WIDTH.wide + REFLOW_MARGIN_PX ? "stacked" : "wide";
}

// play-test: per-group floors under height pressure — multipliers in DESIGN units
// (base = 1), so a floor means "never below this fraction of the design's size",
// whatever the width target was. Content (UX) is deliberately unfloored: Play-visible
// is the hard requirement, so when even collapsed air can't fit the menu, the content
// itself yields.
const PRESSURE_FLOORS = { UX: 0, VISUAL: 0.5, FILLER: 0.35 };

/** The per-group multipliers at width-target ratio `rw` under height pressure `t`:
 *  every group rests at the uniform width fit (t = 0 — the design proportions hold),
 *  and yields under pressure at its SIZING_GROUPS weight — gaps 4× faster than
 *  tappable content, the wordmark 2× — down to its floor. The solver's weighted
 *  deficit distribution (docs/layout/ENGINE § weighted sizing groups) re-anchored at
 *  the width target: simultaneous and continuous, never sequential. */
function multipliersAt(rw, t) {
  const m = (group) =>
    Math.max(
      PRESSURE_FLOORS[group],
      rw * (1 - SIZING_GROUPS[group].weight * t),
    );
  const ux = m("UX");
  // A floor holds only while the content multiplier sits above it; below, the group
  // follows the content down (min-clamp). This keeps the fit UNCONDITIONAL — menuH
  // still tends to zero as the content collapses, so the Play-visible cap is always
  // reachable on any viewport — and preserves the yield order (air and wordmark never
  // exceed content) at any pressure, not just the tested range.
  return { ux, vis: Math.min(m("VISUAL"), ux), gap: Math.min(m("FILLER"), ux) };
}

/** Push the elastic sizing as custom properties: unitless per-group multipliers the
 *  CSS game-start section multiplies its base px by (the `--result-gap-scale`
 *  precedent), plus the column width as real px so CSS carries no width math.
 *  `--gs-menu-width` is written from `rw` (the REST width-fit ratio), not `mult.ux`
 *  (A1 width decouple): the column's width is a pure function of vw, never of height
 *  pressure — `--gs-ux` keeps its full pressured solve to scale content only. At rest
 *  (t = 0) `mult.ux === rw`, so the two coincide and every unpressured viewport is
 *  byte-identical to the pre-decouple form by construction. */
function applyStartElasticSizing(content, arrangement, mult, rw) {
  const st = content.style;
  st.setProperty("--gs-ux", `${mult.ux}`);
  st.setProperty("--gs-vis", `${mult.vis}`);
  st.setProperty("--gs-gap", `${mult.gap}`);
  st.setProperty("--gs-menu-width", `${BASE_WIDTH[arrangement] * rw}px`);
}

/** Menu height = bottom of the last menu element (the Play button, the leaderboard's
 *  previous sibling) at the CURRENT elastic sizes; it excludes the trailing column gap,
 *  so with a leaderboard the fit still sizes the menu to exactly its no-leaderboard
 *  extent. No board → the whole content is the menu. offsetTop resolves against the
 *  content node (its offsetParent — the nearest positioned ancestor).
 *
 *  Subtracts the multiplayer section's panel height, when present (game-start-multiplayer-
 *  section.js § "the panel is outside the height solve"): the panel sits mid-column, between the
 *  mode cards and the control bank, so its real rendered height would otherwise inflate this
 *  measure whenever it's expanded — re-solving the whole composition on every expand/collapse.
 *  Subtracting its own `offsetHeight` back out makes the solver always size the rest of the
 *  column as-if-collapsed: a no-op when the panel is genuinely collapsed (0px), and it cancels
 *  the panel's real contribution when expanded (round-1 spec finding 1). */
function measureMenuH(content) {
  const leaderboard = content.querySelector(".game-start-leaderboard");
  const menuLast =
    leaderboard != null ? leaderboard.previousElementSibling : null;
  const raw =
    menuLast != null
      ? menuLast.offsetTop + menuLast.offsetHeight
      : content.offsetHeight;
  const multiplayerPanel = content.querySelector(MULTIPLAYER_PANEL_SELECTOR);
  return multiplayerPanel != null ? raw - multiplayerPanel.offsetHeight : raw;
}

/**
 * Pure fit predicate (start-screen-unfolded-menu-strip design): does a labeled single row of
 * `labelWidths.length` equal-width tiles fit inside `columnWidth`? `effectiveSizes` carries the
 * strip's own geometry at the CURRENT elastic px (`iconPx`/`tileGapPx`/`tilePaddingPx`/
 * `rowGapPx`/`borderPx` — the last one FIXED/non-elastic, see `STRIP_TILE_BORDER_PX` above) —
 * already multiplier-applied, so this function does no scaling math itself, only geometry
 * addition. Every field is required (review finding: an optional, defaulting-to-0 `borderPx`
 * would silently under-count for any future caller that forgot it — the exact bug class this
 * term exists to fix — and there is only one caller today, which always has a real value; no
 * caller earns a default). No layout APIs (jsdom has no offsetWidth) — canvas `measureText` is
 * the only measurement involved, and that's the CALLER's job (the `prompt-ui.text-metrics.js`
 * precedent); this predicate stays pure so the labeled↔wrap decision can't oscillate with its
 * own effect — same inputs, same answer, every time.
 */
export function fitsLabeled(columnWidth, labelWidths, effectiveSizes) {
  const { iconPx, tileGapPx, tilePaddingPx, rowGapPx, borderPx } =
    effectiveSizes;
  const tileCount = labelWidths.length;
  const availablePerTile =
    (columnWidth - rowGapPx * (tileCount - 1)) / tileCount;
  const widestTileNeeds =
    Math.max(...labelWidths) +
    iconPx +
    tileGapPx +
    tilePaddingPx * 2 +
    borderPx;
  return widestTileNeeds <= availablePerTile;
}

/** The pure question the strip decision writes the answer of: at this elastic `--gs-ux` and the
 *  committed `--gs-menu-width`, would a labeled single row OVERFLOW the column? Reads each tile's
 *  real label text out of the DOM rather than re-deriving it from menu-entries.js, so the answer
 *  is locale-honest by construction with no second import of the entry table.
 *
 *  Split out from `decideStripWrap` for a second real caller: the re-fit skip
 *  (`reservedWithoutResolving`) must ASK this question without writing its answer — label glyph
 *  widths are the one input of it that the skip's `menuH` key provably does not cover. Affordable
 *  there because the whole cost is a canvas `measureText` per tile plus one inline-style read: no
 *  layout API is touched, so it forces no recalc on a path whose point is a single batched
 *  measure. */
function stripWouldWrap(strip, content, ux) {
  const labelPx = plateauedSize(
    STRIP_LABEL_BASE_PX,
    STRIP_LABEL_PLATEAU_PX,
    ux,
  );
  const labelWidths = [...strip.querySelectorAll(".gs-strip-tile-label")].map(
    (el) => measureText(el.textContent, labelPx),
  );
  const columnWidth = parseFloat(
    content.style.getPropertyValue("--gs-menu-width"),
  );
  return !fitsLabeled(columnWidth, labelWidths, stripEffectiveSizes(ux));
}

/** `fitsLabeled`'s `effectiveSizes` — the strip's own geometry at this elastic ux, each term the
 *  `min(plateau, base * ux)` shape the constants above carry. Its own function only so the
 *  predicate above stays readable around it (CODING § method extraction floor: a coherent
 *  intermediate concept, already named by the parameter it feeds). */
function stripEffectiveSizes(ux) {
  return {
    iconPx: plateauedSize(STRIP_ICON_BASE_PX, STRIP_ICON_PLATEAU_PX, ux),
    tileGapPx: plateauedSize(STRIP_TILE_GAP_PX, STRIP_TILE_GAP_PLATEAU_PX, ux),
    tilePaddingPx: plateauedSize(
      STRIP_TILE_PADDING_PX,
      STRIP_TILE_PADDING_PLATEAU_PX,
      ux,
    ),
    rowGapPx: plateauedSize(STRIP_ROW_GAP_PX, STRIP_ROW_GAP_PLATEAU_PX, ux),
    borderPx: STRIP_TILE_BORDER_PX, // fixed — border-width doesn't scale with ux, see the const
  };
}

/** The strip decision (A1 pipeline step 5, start-screen-layout-improvements) — what remains of
 *  the old two-pass decideStripWrap once canonicalize + the provisional solve hoisted up into
 *  `placeStartAssembly` (steps 2-3, shared with the compact decision below): asks
 *  `stripWouldWrap` against the COMMITTED `--gs-ux` — post-compact-decision, not the provisional
 *  `pressuredUx0` — since the compact re-solve can push ux well above the provisional value
 *  (row-axis cards are shorter), and writes the answer as `.gs-strip--wrap`. REST rw alone is not
 *  a safe proxy for either ux either, since the strip's own sizing is `min(plateau, base * ux)`:
 *  the plateaued terms stay FLAT above their own crossover ux while `--gs-menu-width` keeps moving
 *  with vw regardless, so a REST-decided "fits" can go stale once pressure pulls the real ux down
 *  through that mismatch — measured: tablet-landscape German (1280×668, a declared viewports.js
 *  cell), REST ux 1.75 said "fits", the real pressured ux 1.34 didn't, and two labels ellipsized.
 *
 *  Returns the decision's OWN terms — `{ux, wrapped, changed}` — or null when no strip is mounted
 *  (this file's own unit tests build a bare `.game-start-content`; the same guard shape as
 *  measureMenuH's multiplayer-panel check above). `changed` lets the caller skip the redundant
 *  re-solve when the class didn't flip (the common case: a desktop-class viewport deciding "no
 *  wrap" against an already-unwrapped strip). `ux` and `wrapped` are what the memo records, so the
 *  re-fit skip can re-ask the same question at the same reference — see `reservedWithoutResolving`.
 *
 *  Direction of the fit, derived (review, third round — the previous comment here had it
 *  backwards): every term of `widestTileNeeds` except the fixed border is a `plateauedSize` of ux,
 *  and so is `rowGapPx`, whose shrink only WIDENS `availablePerTile`; `columnWidth` is
 *  ux-independent by the A1 width decouple. So a LOWER ux makes the labeled fit EASIER, not
 *  harder — wrapping adds height, which lowers ux, which argues for unwrapping again. The reason
 *  that cannot oscillate is STRUCTURAL, not monotone: the pipeline decides once, against a
 *  canonicalized state, and never re-evaluates the decision against its own effect. The one
 *  re-solve on a flip re-fits the height to the decided shape; it does not re-open the decision. */
function decideStripWrap(content) {
  const strip = content.querySelector(".gs-strip");
  if (strip == null) return null;
  const ux = parseFloat(content.style.getPropertyValue("--gs-ux"));
  const wrapped = stripWouldWrap(strip, content, ux);
  const changed = strip.classList.contains("gs-strip--wrap") !== wrapped;
  strip.classList.toggle("gs-strip--wrap", wrapped);
  return { ux, wrapped, changed };
}

/** When the width-fit menu overflows the height cap, raise the pressure until it
 *  fits, leaving the fitting sizes applied. menuH(t) is monotone-decreasing and
 *  piecewise-linear (each floor is a kink), so bracket-then-bisect converges
 *  unconditionally — no slope estimate to go stale across a kink. */
function solveHeightPressure(content, arrangement, rw, cap) {
  const menuHAt = (t) => {
    applyStartElasticSizing(content, arrangement, multipliersAt(rw, t), rw);
    return measureMenuH(content);
  };
  if (measureMenuH(content) <= cap + 1) return; // +1: sub-px slack, here and below
  let lo = 0;
  // why: 0.25 starts the bracket past the observed real-viewport range (t ≈ 0.24 at the
  // tightest oracle case), so most pressured fits bisect immediately; the ×2 expansion
  // reaches t = 1 — where the UX multiplier hits 0 and menuH bottoms out, so the
  // bracket ALWAYS closes — in 3 of its 5 allowed doublings.
  let hi = 0.25;
  for (let i = 0; i < 5 && menuHAt(hi) > cap + 1; i++) {
    lo = hi;
    hi *= 2;
  }
  // why: 10 halvings shrink the bracket to ≤0.002 in t — sub-pixel on any real menu
  // (weight 4 × 0.002 × a ~560px base ≈ 4px of gap extent, split across the column).
  for (let i = 0; i < 10 && hi - lo > 0.002; i++) {
    const mid = (lo + hi) / 2;
    if (menuHAt(mid) > cap + 1) lo = mid;
    else hi = mid;
  }
  menuHAt(hi); // land on the fitting side of the bracket
}

/** Sizes and re-splays the top-right Menu-trigger affordance (`.menu-button-start`,
 *  game-start-ui.js) — NOT this file's own "menu" (the start-screen content column:
 *  BASE_WIDTH, --gs-menu-width, measureMenuH). `.game-start` always mounts as a direct
 *  child of #skat-app (game-start-ui.js#renderGameStart appends both there), so `screen`'s
 *  parent reaches the same node board-layout.js writes the in-game button's vars onto —
 *  applyMenuAffordanceVars is the single writer both paths call, so the two buttons can
 *  never size apart. Split out of `placeStartAssembly` on size (function-budget, function
 *  extraction floor — a single-caller helper named for a coherent intermediate concept). */
function applyMenuButtonAffordance(screen, vw, vh) {
  const appEl = screen.parentElement;
  applyMenuAffordanceVars(appEl, vw, vh);
  // The button's rect just moved (its --menu-size just changed) — refresh its
  // sun-anchored rest splay on every pass, including the very first (armGlare, wired
  // from game-start-ui.js's own render, re-sweeps too — this just keeps every later
  // resize in sync without re-arming the pointer listener).
  //
  // "Every pass" means every SOLVING pass: the unchanged-inputs skip
  // (`reservedWithoutResolving`) bypasses this whole function, and soundly — on the
  // start screen `sunInViewport` has no oval to anchor to, so it falls back to the
  // viewport golden cut, and no tile's own rect moves when content grows below the
  // fold (`.game-start-content` is absolutely positioned, the leaderboard flows under
  // the strip). The one value that would have moved is `_glareCentre` (#skat-app's own
  // box centre, which tracks the reserved scroll height) — and freezing it at the
  // menu-height box is the better read anyway, since a 3000px-tall document would put
  // that centre far below the fold and clamp `onGlareMove`'s tilt to one extreme.
  refreshGlareAngles(appEl);
}

/** Canonicalize (A1 pipeline step 2, start-screen-layout-improvements): clears both per-pass
 *  decision states — `data-compact` and `.gs-strip--wrap` — before the provisional solve runs,
 *  so the compact and strip decisions below are PURE functions of this pass's own canonicalized
 *  state, never of a decision either one made on a prior pass. Without this, the provisional
 *  solve would run against whatever state the screen already carries from mount or a prior
 *  resize, and a decision that adds height (wrapping, or the compact re-solve) is self-
 *  reinforcing — more height raises pressure, which lowers ux, which can keep a borderline
 *  decision on the engaged side purely because it arrived already engaged (round-2 review
 *  finding against the strip decision, the same history-dependence risk the compact decision
 *  would carry without this hoist). One deterministic answer per viewport, independent of
 *  history — the decision-purity property the idempotence claim (part C) is built on. */
function canonicalizeDecisionState(screen, content) {
  delete screen.dataset.compact;
  content.querySelector(".gs-strip")?.classList.remove("gs-strip--wrap");
}

/** The compact decision (A1 pipeline step 4, start-screen-layout-improvements) — a pure function
 *  of the canonicalized provisional ux (`pressuredUx0`, discovered by the bare provisional
 *  `solveHeightPressure` call at the caller's step 3), never re-evaluated against its own effect:
 *  engagement is decided ONCE, on the canonical (pre-compact) state — the committed ux the
 *  re-solve below produces (row-axis cards are shorter, so it lands well above the provisional
 *  one) is never checked against the threshold either way. `data-compact` flips only the mode
 *  cards' internal axis and the control bank's alignment (skat.css `[data-compact]` rules) — no
 *  width, no offset; the styling residue demoted from the retired discrete compact regime.
 *  Shape-keyed (any wide-arrangement viewport under heavy height pressure), not device-keyed. */
function applyCompactDecision(
  screen,
  content,
  arrangement,
  rw,
  cap,
  pressuredUx0,
) {
  if (arrangement !== "wide" || pressuredUx0 >= COMPACT_UX_THRESHOLD) return;
  screen.dataset.compact = "true";
  applyStartElasticSizing(content, arrangement, multipliersAt(rw, 0), rw);
  solveHeightPressure(content, arrangement, rw, cap);
}

/** The content's `top` inset (A1's new engine output, start-screen-layout-improvements) —
 *  replaces the static CSS `top: 4vh` with one continuous formula, all viewports, no compact
 *  branch (round-3 design-lens finding: a two-branch form jumped ≥20px at the compact threshold
 *  crossing). Treats the frame padding above `.game-start-content` (`#skat-app`'s own
 *  `APP_FRAME_TOP_PX`) as yieldable air: `pressureYield` is `mult.gap / rw` — 1 at rest (`mult.gap`
 *  itself rests at `rw`, not 1, so the division is load-bearing), falling with pressure — and the
 *  frame-plus-gap total yields by that same fraction before flooring at `TOP_FLOOR_PX`. At rest
 *  (`pressureYield = 1`) this reproduces `24 + 4vh − 24 = 4vh` exactly — the same rest-identity the
 *  width decouple has. Under deep pressure it goes NEGATIVE by design: `.game-start-content` is
 *  positioned below `#skat-app`'s own 24px top padding, so shrinking the effective gap above the
 *  logo toward the 8px floor means climbing above its own containing block.
 *
 *  Written ONCE the pipeline has settled — after the compact and strip decisions, reading the
 *  committed `--gs-gap` back off `content.style` (the existing `parseFloat(content.style
 *  .getPropertyValue(...))` idiom) — never inside `applyStartElasticSizing`, which runs per
 *  bisection step on a hot path that predates the compact decision (the `--gs-row-inset`
 *  staleness class, round-2 finding). */
function computeTopInset(content, vh, rw) {
  const gapMult = parseFloat(content.style.getPropertyValue("--gs-gap"));
  const pressureYield = gapMult / rw;
  const topInset =
    Math.max(
      TOP_FLOOR_PX,
      (APP_FRAME_TOP_PX + vh * TOP_GAP_FRAC) * pressureYield,
    ) - APP_FRAME_TOP_PX;
  content.style.setProperty("--gs-top-inset", `${topInset}px`);
  return topInset;
}

// The last COMMITTED solve — `{content, vw, vh, menuH, strip}` (`strip` being
// `decideStripWrap`'s own returned terms, or null when no strip is mounted), recorded only
// once a pass has run the full pipeline past the pre-mount bail (so a jsdom/pre-mount pass,
// where every offset dim is 0, can never leave a memo for a later pass to match "0 === 0"
// against). Keyed on the node itself: `renderGameStart` rebuilds `.game-start-content` on
// every return from a game, and a rebuilt node's solve shares nothing with the old one's.
let lastSolve = null;

/** The re-fit skip (a): when this pass's inputs are provably unchanged since the last
 *  committed solve, do the cheap TAIL only — re-reserve the in-flow scroll height — and
 *  report that the pass is handled. Returns false to let the full pipeline run.
 *
 *  Why this is safe by CONSTRUCTION rather than by enumerating what changed: the memo
 *  keys on the exact quantity the solve converges on. `measureMenuH` is what
 *  `solveHeightPressure` bisects against, so ANY change that could move the fit — a name
 *  edit, a font swap, a locale string, the multiplayer BAR's turn-count badge, the
 *  reference strip, the mode cards, the control bank — moves this number and takes the
 *  full path. The two growths that dominate a data-rich entrance provably cannot move it,
 *  because `measureMenuH` structurally excludes them: leaderboard rows land BELOW
 *  `menuLast`, and the multiplayer PANEL's own height is subtracted back out. vw/vh cover
 *  the viewport, and the arrangement with them — it is a pure function of vw
 *  (`arrangementFor`).
 *
 *  Why one committed sample is enough, rather than the whole `menuH(t)` curve the solve
 *  bisects: the solve converges to the crossing of a MONOTONE `menuH(t)` against a cap
 *  that is a pure function of vh. Re-measuring at the committed `t` and finding the same
 *  value means the same `t` still solves the same equation — so the height fixed point is
 *  unchanged, and with it the compact decision and the top inset, which are functions of
 *  that solve alone.
 *
 *  The STRIP decision is the one that isn't, so it is asked again here. Its predicate
 *  (`stripWouldWrap`) has three inputs: the column width (a pure function of vw — covered),
 *  the tiles' plateaued sizes (functions of the ux the decision was made at — covered), and
 *  the labels' GLYPH widths, which nothing above covers. A label's line box is
 *  `font-size × a unitless line-height` and every other tile term is a `plateauedSize` of
 *  ux, so glyph widths move no height at all: a font swap (canvas `measureText` resolves
 *  the `"Satoshi", system-ui` STACK against whatever is loaded at call time) can change the
 *  labeled fit while leaving menuH exactly where it was, and the labels would ellipsize —
 *  the failure `decideStripWrap` was built against. Re-asking is cheap and forces no
 *  layout, so the skip checks it rather than arguing from what is currently reachable.
 *
 *  It re-asks at the DECISION'S OWN ux, recorded in the memo — not at whatever `--gs-ux`
 *  currently reads. The two are the same ux only when the strip decision didn't flip the
 *  class; when it did, the pipeline re-solved against the newly-wrapped shape and committed
 *  a LOWER ux than the one `decideStripWrap` ran at (wrapping adds height, which raises
 *  pressure). Asking at that lower ux would compare a biased answer — a lower ux makes the
 *  labeled fit easier, see `decideStripWrap`'s derivation — against a class decided at the
 *  higher one, so a wrapping viewport near the boundary would mismatch on every observer
 *  fire and never skip. Re-asking at the recorded ux leaves glyph widths as the only thing
 *  that can differ between the two calls, which is exactly the hole this check exists for —
 *  and it needs no special case for a viewport that never wraps, since there the recorded ux
 *  simply is the committed one.
 *
 *  That is also why the leaderboard SECTION appearing or disappearing needs no special
 *  case, even though it flips `measureMenuH`'s measurement basis
 *  (`menuLast.offsetTop + offsetHeight` with a board, `content.offsetHeight` without):
 *  the key is MEASURED, not classified. If the flip moves the extent, the pipeline runs;
 *  if it doesn't, the fit provably doesn't need to change. (In a real column the two
 *  bases coincide when there's no board — flex `gap` only exists BETWEEN items — so this
 *  flip normally costs nothing; measured on a Pixel 5 against a seeded 17-row profile,
 *  the section's arrival left the committed extent identical.)
 *
 *  The read is against the ALREADY-COMMITTED DOM state — `data-compact` and
 *  `.gs-strip--wrap` as the last pipeline left them, `--gs-*` at their committed values —
 *  so the comparison is apples-to-apples, and this function writes nothing before it (a
 *  skip asserts nothing changed, so it must not canonicalize either). Every read below is
 *  batched with no write between them: one forced layout for the whole pass, against the
 *  pipeline's ~32. It also runs outside the `.gs-solving` freeze window, which exists
 *  only to stop the pipeline's own writes-then-measures from reading a tweening value —
 *  there are no writes here to protect a measure from.
 *
 *  The cheap tail is the height reserve alone, which is the whole reason the observer
 *  exists (rows landing must extend the page's scroll range). `topInset` is unchanged on
 *  a skip — it is a pure function of vh and the committed `--gs-gap`, both unchanged — so
 *  it is read back off `content.style` rather than re-derived. */
function reservedWithoutResolving(screen, content, vw, vh) {
  if (!START_REFIT_DEFAULTS.skipUnchangedSolves) return false;
  if (lastSolve == null || lastSolve.content !== content) return false;
  if (lastSolve.vw !== vw || lastSolve.vh !== vh) return false;
  // Same sub-px slack the cap comparisons use — a 1px menu move cannot cross a fit the
  // solve itself only resolves to within 1px.
  if (Math.abs(measureMenuH(content) - lastSolve.menuH) > 1) return false;
  // The strip is built with the content node (game-start-ui.js#renderGameStart), so a null
  // memo entry means this node has no strip at all, not that one arrived since.
  if (lastSolve.strip != null) {
    const { ux, wrapped } = lastSolve.strip;
    const strip = content.querySelector(".gs-strip");
    if (stripWouldWrap(strip, content, ux) !== wrapped) return false;
  }
  const topInset = parseFloat(content.style.getPropertyValue("--gs-top-inset"));
  screen.style.height = `${topInset + content.offsetHeight}px`;
  return true;
}

/** The full solve: width fit → the A1 decision pipeline → the top inset and the in-flow
 *  height reserve. Returns what `placeStartAssembly` memoizes — the committed `menuH` plus
 *  the strip decision's own terms — or null when layout wasn't ready and the pass bailed.
 *  Split out of `placeStartAssembly` on size — a single-caller helper named for a coherent
 *  intermediate concept (CODING § method extraction floor). */
function solveAndPlace(screen, content, arrangement, vw, vh) {
  // Aim the fit at the width target: the comfy column (COMFY_MAX_PX) on a wide
  // viewport, SIDE_FILL of the width on a narrow one. Pressure-independent (A1 width
  // decouple) — --gs-menu-width is a pure function of rw, published here and never
  // revisited by the height solve below; --gs-ux keeps the full pressured solve to
  // scale CONTENT only. At rest (pressure 0) every group sits at this ratio uniformly
  // — same geometry the settled width-fill model rendered, now at real px.
  const target = Math.min(COMFY_MAX_PX, vw * SIDE_FILL);
  const rw = target / BASE_WIDTH[arrangement];
  applyStartElasticSizing(content, arrangement, multipliersAt(rw, 0), rw);
  const cap = vh * HEIGHT_FILL;

  // The decision pipeline (A1, start-screen-layout-improvements): each decision is a
  // PURE function of a canonicalized provisional solve, never re-evaluated against its
  // own effect — no fixed-point iteration, so no oscillation can exist. Steps 2-3
  // (canonicalize, the bare provisional solve) are shared by the compact decision
  // (step 4) and the strip decision (step 5, decideStripWrap below) — offsetWidth/
  // offsetHeight are still 0 in a pre-mount/jsdom context here, but solveHeightPressure
  // no-ops there (measureMenuH reads 0 either way), so it's safe to run this whole
  // pipeline before the bail check.
  canonicalizeDecisionState(screen, content);
  solveHeightPressure(content, arrangement, rw, cap);
  const pressuredUx0 = parseFloat(content.style.getPropertyValue("--gs-ux"));
  applyCompactDecision(screen, content, arrangement, rw, cap, pressuredUx0);

  // Layout not ready (pre-mount / jsdom): offset dims are 0 — bail after the
  // arrangement + sizing + decision writes (deterministic props, no NaN), before any
  // further measure-driven work.
  if (content.offsetWidth === 0 || content.offsetHeight === 0) return null;

  // The strip decision, against the COMMITTED (post-compact) ux — see decideStripWrap's
  // own comment for why. Re-solve for the FINAL committed state only when it actually
  // flipped the class — the common case (a desktop-class viewport deciding "no wrap"
  // against an already-unwrapped strip, or any viewport where the pipeline's own answer
  // already matches reality) needs no further work. When it DID flip, the strip's own
  // height just changed (a 3×2 grid is taller than one labeled row), so reset to REST
  // and re-run the cap-fit bisection against the now-decided shape (honoring whatever
  // data-compact the compact decision above already committed to). Cap by HEIGHT so the
  // MENU always fits and the Play CTA is on screen without scrolling — a hard
  // requirement. `menuH` excludes the leaderboard → the cap tracks the menu, not the
  // scrolling table below.
  const strip = decideStripWrap(content);
  if (strip?.changed) {
    applyStartElasticSizing(content, arrangement, multipliersAt(rw, 0), rw);
    solveHeightPressure(content, arrangement, rw, cap);
  }

  // Both reads first, then the write: `--gs-top-inset` moves the content's `top`, which
  // changes neither the menu extent (menuLast.offsetTop resolves against content, its
  // offsetParent) nor content's own height, so reading before it is equivalent — and it
  // costs one forced layout for the pair instead of one each.
  const menuH = measureMenuH(content);
  const contentH = content.offsetHeight;
  // The engine's one new output (A1): the content's top inset, replacing the static CSS
  // top: 4vh — computed once the pipeline has settled (computeTopInset's own header has
  // the write-site reasoning). The in-flow height reserve uses the same value so the page
  // scrolls to reveal the leaderboard (the absolute content adds nothing to scrollHeight
  // on its own) — one producer, no drift between the two.
  screen.style.height = `${computeTopInset(content, vh, rw) + contentH}px`;
  return { menuH, strip };
}

/**
 * Place + size the game-start assembly as one self-contained pass. Idempotent —
 * runs on first render, on every resize, and on the async re-measure (the elastic
 * writes converge to the same fixed point for a given viewport, so the observer
 * settles after one echo).
 */
export function placeStartAssembly(screen) {
  const content = screen.querySelector(".game-start-content");
  if (content == null) return;

  // Track the content node so the async re-measure re-fits when it grows (the
  // leaderboard's rows land after this first synchronous fit). Rebinds only on a
  // genuine node change — see trackContentNode.
  trackContentNode(content);

  // Same visible-viewport-over-layout-viewport reasoning as the vh read just below, but
  // for a different failure mode: `.game-start-content`'s CSS fallback width
  // (`--gs-menu-width, 560px`, before this pass ever writes the var) overflows a narrow
  // mobile viewport horizontally in the pre-solve paint. The browser responds by
  // EXPANDING the layout viewport to fit that overflow (observed innerWidth 412 → 421 on
  // a 412px device) while visualViewport.width stays pinned at the true visible width —
  // so a vw read landing mid-transition would solve against the inflated width, paint
  // that overflowing geometry, and only converge to the correct fit on a later resize
  // echo (three counted CLS shifts, initial-load-cls-regression). visualViewport is
  // immune to this because it tracks the visible area, not the document's overflow.
  // Known accepted delta: on desktop windows narrower than ~1065px with a classic
  // (non-overlay) scrollbar, visualViewport.width excludes the scrollbar's width where
  // innerWidth doesn't, so the solved column can land up to ~14px narrower than before —
  // moot above that width, where the COMFY_MAX_PX cap governs instead.
  const vw = window.visualViewport?.width ?? window.innerWidth;
  // The SMALL viewport height (100svh — bar-shown), not the visible or layout viewport
  // (part C, start-screen-layout-improvements — bar-stable solve): 100svh is CONSTANT
  // across a Chrome-Android address-bar show/hide transition, where visualViewport.height
  // itself moves as the bar animates, so fitting to it means a bar transition re-solves
  // this whole pass to IDENTICAL outputs (the pure-function decision pipeline below is what
  // makes that idempotence claim hold for the decisions, not just the multipliers) instead
  // of visibly re-scaling the composition. It also needs no keyboard redirect, unlike
  // stableViewportHeight() (viewport-settle.js): viewport units resolve under the page's
  // default `resizes-visual` (no `interactive-widget` meta), so the on-screen keyboard never
  // moves 100svh either. Known, accepted limitation: with the bar hidden the composition is
  // sized for the bar-SHOWN height (~6-10% smaller than it could be) — Play-visible is the
  // hard requirement, stability wins, desktop is unaffected (svh === lvh there). Fullscreen
  // entry/exit legitimately re-solves (svh grows to lvh) — a deliberate mode change, not a
  // regression (viewport-settle.js#smallViewportHeight's own header has the never-cache
  // rule this shares with fullscreen.js#canGainHeight).
  const vh = smallViewportHeight();
  // Everything below this line is a no-op when nothing the solve depends on moved — the
  // affordance vars and the arrangement are pure functions of vw/vh, and the freeze
  // window only exists to protect the solve's own measures. See the header's (a).
  if (reservedWithoutResolving(screen, content, vw, vh)) return;
  applyMenuButtonAffordance(screen, vw, vh);
  const arrangement = arrangementFor(vw);
  screen.dataset.arrangement = arrangement;

  // Freeze motion for the synchronous solve window (no paint happens inside it): under
  // `prefers-reduced-motion` the global 0.01ms-duration rule puts a transition on EVERY
  // property, so each measure would report the pre-write layout and the solve would
  // diverge. Same fix as the result assembly's `.result-solving` — see the CSS rule.
  screen.classList.add("gs-solving");
  try {
    const solved = solveAndPlace(screen, content, arrangement, vw, vh);
    // Only a pass that ran the pipeline to completion records a memo — see lastSolve.
    if (solved != null) lastSolve = { content, vw, vh, ...solved };
  } finally {
    screen.classList.remove("gs-solving");
  }
}

// The async re-measure. The leaderboard loads after the first synchronous fit
// (`/api/leaderboard`), so its rows inflate the composition's height. The fit is
// leaderboard-independent (the height cap tracks `menuH`, not the board), so the rows
// don't resize the menu — but they DO extend the content below the fold, and the
// reserved in-flow `screen.height` (set from `topInset + content.offsetHeight`,
// computeTopInset's own producer) must grow to match or the page won't scroll far
// enough to reveal the board. A single
// module-scoped ResizeObserver re-runs the fit whenever `.game-start-content` changes
// size (rows landing, a name edit, a font swap), re-reserving that height. Module-
// scoped, not per-screen: `renderGameStart` rebuilds the content node on every return
// from a game, so the observer must follow the CURRENT node.
let contentObserver = null;
let observedContent = null;
// why: ResizeObserver always delivers one initial observation shortly after .observe(),
// whether or not the box actually changes size afterward (board-layout.js#observeLayoutChanges
// carries the same guard, `initialFireSeen`, against the same platform behavior — its comment
// there has the full class, though there the observed node's size is stable across the call;
// here it isn't — `trackContentNode` is called before the elastic writes below run, so this
// node's box does change size during this very pass). Either way that first delivery reports
// the box's state as of the fit that just ran, which the height reserve above already
// accounts for — honoring it anyway re-runs placeStartAssembly redundantly right after mount:
// a solveHeightPressure bisection with forced layout, landing inside the entrance
// choreography (docs/PERFORMANCE.md § Choreography frames stay clean). Reset on every
// rebind (see below) — each observe() call delivers its own initial observation, not just
// the first one ever.
let initialFireSeen = false;

// The entrance-window deferral (b, start-screen-entrance-stutter). A content re-fit that
// genuinely IS due — the leaderboard section arriving, the multiplayer panel landing —
// costs a full ~32-measure pipeline, and on a data-rich session several of them fire
// while the entrance choreography is still playing (docs/PERFORMANCE.md § Choreography
// frames stay clean). Deferred to the entrance's own furniture-settled point and
// coalesced: N mid-entrance triggers cost exactly one re-fit, after the choreography.
// Screen identities, not booleans — renderGameStart builds a fresh screen on every
// return from a game, each with its own entrance.
let deferredRefitScreen = null;
let settledEntranceScreen = null;

/** Route an observer-driven re-fit around the entrance window: returns true when the
 *  re-fit has been QUEUED for the furniture-settled point (so the caller must not run it
 *  now), false when it should run immediately.
 *
 *  It runs immediately when no entrance is in flight — `_furnitureSettled` absent (no
 *  entrance ever ran on this screen) or this screen's window already closed. The
 *  no-intro/reduced-motion bail resolves that promise too (bailEntranceWithoutIntro), as does
 *  the build's own catch, so a queued re-fit still fires on both.
 *
 *  ONE path leaves it pending, known and accepted (round-3 review): a Play click landing
 *  between the intro settling and the leaderboard beat makes `finishEntranceEtch`
 *  (game-start-entrance.js) kill `_leaderboardReveal`, and `furnitureSettled()` is called
 *  from INSIDE that delayed callback — so it never runs, and a re-fit queued here waits
 *  forever while later triggers coalesce into the dead queue. Bounded, with no residue — and
 *  the bound is a PROPERTY, not a route: `renderGameStart` has exactly two production callers
 *  — `boot.js#renderStartScreen` and `game.js#renderGameStartScreen` (enumerated 2026-08-24;
 *  a grep also hits `dev/background-lab.js`, the standalone dev page, which is not a route a
 *  player reaches). Both build a fresh content node, which `trackContentNode` turns into a
 *  `forgetRetiredScreenState`, so no dead queue outlives the rebuild — a FAILED session create
 *  included. Phrased as the property deliberately: three review rounds were spent describing
 *  the control flow that reaches those two callers, and every description was wrong in a
 *  different way. The caller set is what the bound rests on. Not worth
 *  pre-empting from this side either: resolving
 *  `furnitureSettled` in the kill would let `playLeaderboardReveal`'s row cascade fire over
 *  the disassembly, which is the very thing the kill exists to prevent. Never fired
 *  against a detached tree: renderGameStart rebuilds the screen on every return, the same
 *  class trackContentNode's disconnect()-on-rebind guards — and here it is a THROW, not
 *  just wasted work, since `applyMenuButtonAffordance` reaches through the screen's
 *  parentElement, which a detached screen doesn't have.
 *
 *  Only the CONTENT observer routes through here. `ensureStartReflowWired`'s viewport
 *  path deliberately does not — a viewport change moves the fit itself, so deferring it
 *  would leave the composition visibly mis-sized for the whole entrance. */
function refitAtEntranceSettle(screen) {
  if (!START_REFIT_DEFAULTS.deferToEntranceSettle) return false;
  const settled = screen._furnitureSettled;
  if (settled == null || settledEntranceScreen === screen) return false;
  if (deferredRefitScreen === screen) return true; // already queued — coalesce
  deferredRefitScreen = screen;
  void settled.then(() => {
    // A rebuild may have retired this screen while its promise was still pending:
    // forgetRetiredScreenState clears the two vars below, but it cannot cancel a
    // reaction already attached here. Without this identity check a stale resolution
    // would null the CURRENT screen's queued flag, and its next trigger would attach a
    // second reaction — two full re-fits at one settle, breaking the coalescing this
    // whole function exists for.
    if (deferredRefitScreen !== screen) return;
    deferredRefitScreen = null;
    settledEntranceScreen = screen; // the window is closed — later triggers run at once
    if (screen.isConnected) placeStartAssembly(screen);
  });
  return true;
}

// A renderGameStart rebuild retires the old content node and its screen: the rebuilt
// screen brings its own entrance and its own solve, so both the solve memo and the
// entrance-window bookkeeping start over rather than leaving detached nodes pinned here.
// (A re-fit already queued for the old screen self-cancels when its own promise finally
// resolves — refitAtEntranceSettle's identity check, then its isConnected guard.)
function forgetRetiredScreenState() {
  lastSolve = null;
  deferredRefitScreen = null;
  settledEntranceScreen = null;
}

function trackContentNode(content) {
  if (contentObserver == null) {
    // The callback re-fits whatever `.game-start` is mounted NOW — it must not close
    // over a `screen`/`content` captured at wire time, because the content node is
    // rebuilt on every return (unlike game.js:564's observer, which safely closes
    // over the never-rebuilt `#skat-app`). A captured node would go stale on rebuild.
    contentObserver = new ResizeObserver(() => {
      if (!initialFireSeen) {
        initialFireSeen = true;
        return;
      }
      const screen = document.querySelector(".game-start");
      if (screen == null) return;
      if (!refitAtEntranceSettle(screen)) placeStartAssembly(screen);
    });
  }
  // Rebind only on a genuine node change (a renderGameStart rebuild), never on every
  // fit: `observe()` delivers a fresh initial observation shortly after each call
  // (see game.js:564), so re-observing the same node inside an observer-triggered
  // re-fit would self-schedule unboundedly. `disconnect()` also flushes any delivery
  // still queued for the old node, so a rebuild can't re-fit a detached tree.
  //
  // Debounce asymmetry, deliberate: unlike board-layout.js's viewport-driven observer
  // (150ms coalescing), this one watches the content node, not the viewport — rows
  // landing or a name edit aren't a bouncy signal, so no debounce is added here. Don't
  // "fix" this to match the board sibling.
  if (content !== observedContent) {
    contentObserver.disconnect();
    initialFireSeen = false; // the rebound node's own observe() delivers a fresh initial fire
    contentObserver.observe(content);
    observedContent = content;
    forgetRetiredScreenState();
  }
}

// Re-places the current start screen (if any) once the visualViewport resize train
// settles (viewport-settle.js) — a bar hide/show still fires this (the resize TRAIN
// visualViewport carries through the transition), even though placeStartAssembly's own
// vh read no longer sources from visualViewport.height (part C — it reads the constant
// 100svh instead, so a bar transition now re-solves to IDENTICAL outputs rather than
// visibly re-scaling). Coalesced so an address-bar bounce fires at most one re-solve
// instead of one per intermediate frame. Registered once, lazily — the screen is
// rebuilt on every return, but the listener outlives it and no-ops when no
// `.game-start` is mounted. Catches VIEWPORT changes, which move the width fit target
// without changing the content's natural box (so the observer alone would miss them).
let reflowWired = false;
export function ensureStartReflowWired() {
  if (reflowWired) return;
  reflowWired = true;
  onVisualViewportSettled(() => {
    const screen = document.querySelector(".game-start");
    if (screen != null) placeStartAssembly(screen);
  });
}
